import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { getSharedBinaryAtlas } from '../../rendering/binary/sharedBinaryResources.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';

/**
 * Distant skyline impostors + subtle green dust motes (follows player, low cost).
 */
export class AtmosphereEffects {
  /** @param {{ config?: typeof CONFIG.atmosphere }} [options] */
  constructor({ config = CONFIG.atmosphere } = {}) {
    this.config = config;
    this.group = new THREE.Group();
    this.group.name = 'AtmosphereEffects';

    this._atlas = getSharedBinaryAtlas();
    this._silhouetteMaterial = new BinaryMaterial({
      atlas: this._atlas,
      palette: 'building',
      overrides: {
        glowStrength: CONFIG.luminance.accentGlowStrength * 0.35,
        scrollSpeed: 4.2,
        flickerStrength: 0.22,
        cellsPerMeter: 3,
      },
    });

    /** @type {THREE.Points | null} */
    this._particles = null;
    /** @type {THREE.Group} */
    this._skyline = new THREE.Group();
    this._skyline.name = 'DistantSkyline';
    this.group.add(this._skyline);

    this._lastAnchor = { x: NaN, z: NaN };
    this._cellBase = 900_000;
    this._buildSkylineRing();
    this._buildParticles();
  }

  /**
   * @param {number} delta
   * @param {{ x: number; y: number; z: number }} playerPosition
   */
  update(delta, playerPosition) {
    const { x, y, z } = playerPosition;
    const moveThreshold = this.config.skylineRecenterDistance;
    if (
      Number.isNaN(this._lastAnchor.x) ||
      Math.hypot(x - this._lastAnchor.x, z - this._lastAnchor.z) > moveThreshold
    ) {
      this._lastAnchor.x = x;
      this._lastAnchor.z = z;
      this._skyline.position.set(x, 0, z);
    }

    this.group.position.set(0, 0, 0);
    this._silhouetteMaterial.update(delta);

    if (this._particles) {
      const attr = this._particles.geometry.getAttribute('position');
      const arr = attr.array;
      const t = performance.now() * 0.001;
      for (let i = 0; i < attr.count; i += 1) {
        const ix = i * 3;
        arr[ix + 1] += Math.sin(t + i * 0.17) * delta * 0.08;
      }
      attr.needsUpdate = true;
      this._particles.position.set(x, y + 1.2, z);
    }
  }

  setEventBoost(boost) {
    this._silhouetteMaterial.setEventBoost(boost);
  }

  dispose() {
    this._silhouetteMaterial.dispose();
    this._particles?.geometry.dispose();
    /** @type {THREE.Material} */ (this._particles?.material)?.dispose();
    for (const child of this._skyline.children) {
      child.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
        }
      });
    }
    this._skyline.clear();
    this.group.clear();
  }

  _buildParticles() {
    const count = this.config.particleCount;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      positions[i * 3] = (Math.random() - 0.5) * this.config.particleSpread;
      positions[i * 3 + 1] = Math.random() * this.config.particleHeight;
      positions[i * 3 + 2] = (Math.random() - 0.5) * this.config.particleSpread;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color: CONFIG.matrix.primaryGreen,
      size: this.config.particleSize,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this._particles = new THREE.Points(geometry, material);
    this._particles.frustumCulled = false;
    this.group.add(this._particles);
  }

  _buildSkylineRing() {
    const radius = this.config.skylineRadius;
    const count = this.config.skylineCount;
    let cell = this._cellBase;

    for (let i = 0; i < count; i += 1) {
      const angle = (i / count) * Math.PI * 2;
      const w = 8 + (i % 4) * 3;
      const h = 28 + (i % 5) * 12;
      const d = 4 + (i % 3);
      const dist = radius + (i % 3) * 18;
      const sx = Math.cos(angle) * dist;
      const sz = Math.sin(angle) * dist;

      const block = new DigitalObject({
        material: this._silhouetteMaterial,
        shape: 'facade',
        size: [w, h, d],
        position: [sx, h / 2, sz],
        cellsPerMeter: 2,
        seed: 7000 + i * 13,
        cellIdBase: cell,
        name: `Skyline_${i}`,
      });
      cell += block.cellsUsed;
      block.group.rotation.y = angle + Math.PI / 2;
      this._skyline.add(block.group);
    }
    this._silhouetteMaterial.setColumns(12);
  }
}
